import { System } from '../System';
import { Entity } from '../Entity';
import { TransformComponent } from '../components/TransformComponent';
import { HealthComponent } from '../components/HealthComponent';
import { CombatComponent } from '../components/CombatComponent';
import { BeastieComponent } from '../components/BeastieComponent';
import { EventBus } from '../../utils/EventBus';

/**
 * CombatSystem - Resolves attacks and applies damage
 * Handles auto-attack when in range of target
 */
export class CombatSystem extends System {
  public readonly requiredComponents = ['transform', 'combat', 'health'];

  public init(): void {
    // Combat system initialized
  }

  public update(entities: Entity[], _delta: number): void {
    const currentTime = Date.now();

    for (const entity of entities) {
      this.processCombat(entity, currentTime);
    }
  }

  /**
   * Process combat for a single entity
   */
  private processCombat(entity: Entity, currentTime: number): void {
    const combat = entity.getComponent<CombatComponent>('combat');
    const transform = entity.getComponent<TransformComponent>('transform');
    const health = entity.getComponent<HealthComponent>('health');

    if (!combat || !transform || !health) return;

    // Skip if dead or fainted
    if (!health.isAlive || health.isFainted) {
      combat.disengage();
      return;
    }

    // Skip if no target
    if (!combat.targetEntityId) return;

    // Get target entity
    const target = this.world.getEntity(combat.targetEntityId);
    if (!target) {
      combat.disengage();
      return;
    }

    const targetTransform = target.getComponent<TransformComponent>('transform');
    const targetHealth = target.getComponent<HealthComponent>('health');

    if (!targetTransform || !targetHealth) {
      combat.disengage();
      return;
    }

    // Check if target is dead or fainted
    if (!targetHealth.isAlive || targetHealth.isFainted) {
      combat.disengage();
      EventBus.emitEvent('combat:target_defeated', {
        attackerId: entity.id,
        targetId: target.id,
      });
      return;
    }

    // Prevent friendly fire - don't attack allies
    if (this.areAllies(entity, target)) {
      combat.disengage();
      return;
    }

    // Check distance to target
    const distance = transform.distanceTo(targetTransform);

    // If in range and cooldown ready, attack
    if (distance <= combat.attackRange && combat.canAttack(currentTime)) {
      this.performAttack(entity, target, currentTime);
    }
  }

  /**
   * Perform an attack on target
   */
  private performAttack(attacker: Entity, target: Entity, currentTime: number): void {
    const attackerCombat = attacker.getComponent<CombatComponent>('combat');
    const targetHealth = target.getComponent<HealthComponent>('health');

    if (!attackerCombat || !targetHealth) return;

    // Calculate damage
    const damage = this.calculateDamage(attacker, target);

    // Apply damage
    const actualDamage = targetHealth.takeDamage(damage, currentTime);

    // Record attack time
    attackerCombat.recordAttack(currentTime);

    // Emit attack event
    EventBus.emitEvent('combat:attack', {
      attackerId: attacker.id,
      targetId: target.id,
      damage: actualDamage,
      targetHealthPercent: targetHealth.healthPercent,
    });

    console.log(
      `${attacker.id} attacks ${target.id} for ${actualDamage} damage (${Math.floor(targetHealth.healthPercent * 100)}% HP remaining)`
    );
  }

  /**
   * Calculate damage for an attack
   * Simple formula: max(1, attack - defense)
   */
  private calculateDamage(attacker: Entity, target: Entity): number {
    // Get attacker's attack stat
    let attackPower = 10; // Default

    const attackerBeastie = attacker.getComponent<BeastieComponent>('beastie');
    const attackerCombat = attacker.getComponent<CombatComponent>('combat');

    if (attackerBeastie) {
      // Use beastie's calculated attack stat
      attackPower = attackerBeastie.getStat('attack');
    } else if (attackerCombat) {
      // Use combat component's base damage
      attackPower = attackerCombat.baseDamage;
    }

    // Get target's defense stat
    let defense = 0; // Default (training dummy has 0)

    const targetBeastie = target.getComponent<BeastieComponent>('beastie');
    if (targetBeastie) {
      defense = targetBeastie.getStat('defense');
    }

    // Simple damage formula
    return Math.max(1, attackPower - defense);
  }

  /**
   * Check if two entities are allies (both party members or both enemies)
   */
  private areAllies(entityA: Entity, entityB: Entity): boolean {
    const aIsParty = entityA.hasTag('party_member') || entityA.hasTag('player');
    const bIsParty = entityB.hasTag('party_member') || entityB.hasTag('player');

    const aIsEnemy = entityA.hasTag('enemy');
    const bIsEnemy = entityB.hasTag('enemy');

    // Both on same team = allies
    return (aIsParty && bIsParty) || (aIsEnemy && bIsEnemy);
  }

  public destroy(): void {
    // No cleanup needed
  }
}
